import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import type { Portfolio, PortfolioCreate, PortfolioList, PortfolioUpdate } from '../types/portfolio'
import { useAuth } from '../hooks/useAuth'

// ============================================================================
// Types
// ============================================================================

interface PortfolioContextValue {
  /** List of all user portfolios */
  portfolios: Portfolio[]
  /** Currently selected portfolio */
  currentPortfolio: Portfolio | null
  /** ID of the current portfolio (for easy access) */
  currentPortfolioId: string | null
  /** User's default portfolio ID */
  defaultPortfolioId: string | null
  /** Loading state */
  isLoading: boolean
  /** Error message if any */
  error: string | null
  /** Switch to a different portfolio */
  switchPortfolio: (portfolioId: string) => void
  /** Create a new portfolio (optionally switch to it, defaults to true) */
  createPortfolio: (data: PortfolioCreate, switchToNew?: boolean) => Promise<Portfolio>
  /** Update an existing portfolio */
  updatePortfolio: (id: string, updates: PortfolioUpdate) => Promise<Portfolio>
  /** Delete a portfolio */
  deletePortfolio: (id: string, force?: boolean) => Promise<void>
  /** Set a portfolio as default */
  setDefaultPortfolio: (id: string) => Promise<Portfolio>
  /** Refresh portfolios from server */
  refreshPortfolios: () => Promise<void>
  /** Handle stale portfolio error - clears selection and refreshes */
  handleStalePortfolioError: (error: unknown) => boolean
}

// ============================================================================
// Context
// ============================================================================

const PortfolioContext = createContext<PortfolioContextValue | undefined>(undefined)

// Local storage key for persisting selected portfolio
const SELECTED_PORTFOLIO_KEY = 'optioneer_selected_portfolio'

// ============================================================================
// Provider
// ============================================================================

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth()
  const userId = user?.id ?? null
  const profileMatchesUser = !!userId && profile?.id === userId
  const hasConfirmedAppAccess = profileMatchesUser
    ? (profile.can_access_app ??
      ['admin', 'premium_active', 'trial_active', 'legacy_free_active'].includes(
        profile.access_status ?? ''
      ))
    : undefined
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [currentPortfolioId, setCurrentPortfolioId] = useState<string | null>(null)
  const [defaultPortfolioId, setDefaultPortfolioId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const portfolioRequestIdRef = useRef(0)

  // Get current portfolio object from the list
  const currentPortfolio = useMemo(
    () => portfolios.find((p) => p.id === currentPortfolioId) ?? null,
    [portfolios, currentPortfolioId]
  )

  // Fetch portfolios from server
  const fetchPortfolios = useCallback(async () => {
    const requestId = ++portfolioRequestIdRef.current

    if (!userId) {
      setPortfolios([])
      setCurrentPortfolioId(null)
      setDefaultPortfolioId(null)
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response: PortfolioList = await api.portfolios.getAll()
      if (requestId !== portfolioRequestIdRef.current) return

      setPortfolios(response.portfolios)
      setDefaultPortfolioId(response.default_portfolio_id)

      if (response.portfolios.length === 0) {
        setCurrentPortfolioId(null)
        localStorage.removeItem(SELECTED_PORTFOLIO_KEY)
        return
      }

      // Restore previously selected portfolio from localStorage, or use default
      const savedPortfolioId = localStorage.getItem(SELECTED_PORTFOLIO_KEY)
      const savedPortfolioExists = response.portfolios.some((p) => p.id === savedPortfolioId)
      const defaultPortfolioExists = response.portfolios.some(
        (p) => p.id === response.default_portfolio_id
      )

      if (savedPortfolioId && savedPortfolioExists) {
        setCurrentPortfolioId(savedPortfolioId)
      } else {
        // Stale portfolio ID detected - clear it and use default
        if (savedPortfolioId && !savedPortfolioExists) {
          console.warn(
            `[PortfolioContext] Stale portfolio_id detected in localStorage: ${savedPortfolioId}. ` +
              `Portfolio no longer exists or is not accessible. Clearing and using default.`
          )
          localStorage.removeItem(SELECTED_PORTFOLIO_KEY)
        }

        const nextPortfolioId =
          response.default_portfolio_id && defaultPortfolioExists
            ? response.default_portfolio_id
            : response.portfolios[0].id

        setCurrentPortfolioId(nextPortfolioId)
        localStorage.setItem(SELECTED_PORTFOLIO_KEY, nextPortfolioId)
      }
    } catch (err) {
      if (requestId !== portfolioRequestIdRef.current) return

      const message = err instanceof Error ? err.message : 'Failed to load portfolios'
      setError(message)
      console.error('Error fetching portfolios:', err)
    } finally {
      if (requestId === portfolioRequestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [userId])

  // Fetch portfolios only after the backend confirms application access.
  // A newly authenticated iOS user reaches the Apple paywall before access is
  // granted, so fetching earlier would fail and would not otherwise retry when
  // that same user activates Premium in the current session.
  useEffect(() => {
    if (!userId) {
      void fetchPortfolios()
      return
    }

    if (hasConfirmedAppAccess !== true) {
      // Invalidate any request started for the previous account/access state so
      // its late response cannot repopulate this provider with stale data.
      portfolioRequestIdRef.current += 1
      setPortfolios([])
      setCurrentPortfolioId(null)
      setDefaultPortfolioId(null)
      setIsLoading(hasConfirmedAppAccess === undefined)
      setError(null)
      return
    }

    void fetchPortfolios()
  }, [fetchPortfolios, hasConfirmedAppAccess, userId])

  // Switch to a different portfolio
  const switchPortfolio = useCallback(
    (portfolioId: string) => {
      const portfolio = portfolios.find((p) => p.id === portfolioId)
      if (!portfolio) {
        toast.error('Portfolio not found')
        return
      }

      setCurrentPortfolioId(portfolioId)
      localStorage.setItem(SELECTED_PORTFOLIO_KEY, portfolioId)
    },
    [portfolios]
  )

  // Create a new portfolio and optionally switch to it
  const createPortfolio = useCallback(
    async (data: PortfolioCreate, switchToNew: boolean = true): Promise<Portfolio> => {
      try {
        const newPortfolio = await api.portfolios.create(data)

        // Add to portfolios list AND switch in one state update batch
        setPortfolios((prev) => [...prev, newPortfolio])

        // Switch to the new portfolio immediately (before state update completes)
        // We do this directly instead of calling switchPortfolio to avoid race condition
        if (switchToNew || !currentPortfolioId) {
          setCurrentPortfolioId(newPortfolio.id)
          localStorage.setItem(SELECTED_PORTFOLIO_KEY, newPortfolio.id)
        }

        // If it's marked as default, update our state
        if (newPortfolio.is_default) {
          setDefaultPortfolioId(newPortfolio.id)
        }

        toast.success(`Portfolio "${newPortfolio.name}" created`)
        return newPortfolio
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create portfolio'
        toast.error(message)
        throw err
      }
    },
    [currentPortfolioId]
  )

  // Update an existing portfolio
  const updatePortfolio = useCallback(
    async (id: string, updates: PortfolioUpdate): Promise<Portfolio> => {
      try {
        const updatedPortfolio = await api.portfolios.update(id, updates)
        setPortfolios((prev) => prev.map((p) => (p.id === id ? updatedPortfolio : p)))
        toast.success('Portfolio updated')
        return updatedPortfolio
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update portfolio'
        toast.error(message)
        throw err
      }
    },
    []
  )

  // Delete a portfolio
  const deletePortfolio = useCallback(
    async (id: string, force: boolean = false): Promise<void> => {
      try {
        await api.portfolios.delete(id, force)
        setPortfolios((prev) => prev.filter((p) => p.id !== id))

        // If we deleted the current portfolio, switch to default or first available
        if (currentPortfolioId === id) {
          const remaining = portfolios.filter((p) => p.id !== id)
          const newCurrent = remaining.find((p) => p.is_default) || remaining[0]
          if (newCurrent) {
            setCurrentPortfolioId(newCurrent.id)
            localStorage.setItem(SELECTED_PORTFOLIO_KEY, newCurrent.id)
          } else {
            setCurrentPortfolioId(null)
            localStorage.removeItem(SELECTED_PORTFOLIO_KEY)
          }
        }

        // If we deleted the default portfolio, refresh to get new default
        if (defaultPortfolioId === id) {
          await fetchPortfolios()
        }

        toast.success('Portfolio deleted')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete portfolio'
        toast.error(message)
        throw err
      }
    },
    [currentPortfolioId, defaultPortfolioId, portfolios, fetchPortfolios]
  )

  // Set a portfolio as default
  const setDefaultPortfolioFn = useCallback(async (id: string): Promise<Portfolio> => {
    try {
      const portfolio = await api.portfolios.setDefault(id)
      setPortfolios((prev) =>
        prev.map((p) => ({
          ...p,
          is_default: p.id === id,
        }))
      )
      setDefaultPortfolioId(id)
      toast.success(`"${portfolio.name}" set as default portfolio`)
      return portfolio
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set default portfolio'
      toast.error(message)
      throw err
    }
  }, [])

  // Refresh portfolios
  const refreshPortfolios = useCallback(async () => {
    await fetchPortfolios()
  }, [fetchPortfolios])

  // Handle stale portfolio error from API calls
  // Returns true if the error was a stale portfolio error and was handled
  const handleStalePortfolioError = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof Error)) return false

      const errorMessage = error.message

      // Check for PORTFOLIO_NOT_FOUND_OR_UNOWNED error code
      const isStalePortfolioError =
        errorMessage.includes('PORTFOLIO_NOT_FOUND_OR_UNOWNED') ||
        (errorMessage.includes('Portfolio not found') && errorMessage.includes('404'))

      if (isStalePortfolioError) {
        console.warn(
          `[PortfolioContext] Stale portfolio detected from API error. ` +
            `Clearing selection and refreshing portfolios.`
        )
        localStorage.removeItem(SELECTED_PORTFOLIO_KEY)
        setCurrentPortfolioId(null)
        // Refresh portfolios to get current list and set default
        void fetchPortfolios()
        return true
      }

      return false
    },
    [fetchPortfolios]
  )

  // Context value
  const value = useMemo<PortfolioContextValue>(
    () => ({
      portfolios,
      currentPortfolio,
      currentPortfolioId,
      defaultPortfolioId,
      isLoading,
      error,
      switchPortfolio,
      createPortfolio,
      updatePortfolio,
      deletePortfolio,
      setDefaultPortfolio: setDefaultPortfolioFn,
      refreshPortfolios,
      handleStalePortfolioError,
    }),
    [
      portfolios,
      currentPortfolio,
      currentPortfolioId,
      defaultPortfolioId,
      isLoading,
      error,
      switchPortfolio,
      createPortfolio,
      updatePortfolio,
      deletePortfolio,
      setDefaultPortfolioFn,
      refreshPortfolios,
      handleStalePortfolioError,
    ]
  )

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access portfolio context
 * @throws Error if used outside of PortfolioProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePortfolio(): PortfolioContextValue {
  const context = useContext(PortfolioContext)
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider')
  }
  return context
}
